import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SERVICES } from "../../data/portfolioData";
import Reveal from "../ui/Reveal";

// Card backgrounds are fixed (not theme tokens) so white text reads in both themes.
const THEMES = {
  rose: "bg-[radial-gradient(120%_90%_at_20%_0%,#ff7aa3_0%,#e8457a_35%,#5a1633_100%)]",
  plum: "bg-[radial-gradient(120%_100%_at_80%_0%,#8a3a8f_0%,#3b1640_55%,#1a0d1f_100%)]",
  coral: "bg-[radial-gradient(120%_100%_at_0%_0%,#ffb199_0%,#f0627f_45%,#6b1d3a_100%)]",
  ink: "bg-[radial-gradient(120%_90%_at_80%_10%,#5a2a44_0%,#2a1a22_45%,#120c10_100%)]",
  berry: "bg-[radial-gradient(120%_100%_at_20%_0%,#d4386b_0%,#7a1f4d_50%,#2a0f22_100%)]",
};

/* ---------- mockups ---------- */

const Phone = () => (
  <div className="w-[230px] rounded-[2rem] bg-[#161214] p-2 shadow-[0_30px_60px_rgb(0_0_0/0.45)] border border-white/10">
    <div className="rounded-[1.6rem] bg-[#f7ede4] h-[300px] overflow-hidden flex flex-col">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-black/5">
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#ff8fb3] to-[#d4386b]" />
        <div className="leading-tight">
          <p className="text-[11px] font-bold text-[#211d1b]">Travel Assistant</p>
          <p className="text-[9px] text-[#211d1b]/50">RAG · online</p>
        </div>
      </div>
      <div className="flex-1 p-3 space-y-2 text-[10px] leading-snug">
        <p className="ml-auto max-w-[75%] rounded-2xl rounded-br-sm bg-[#211d1b] text-[#f7ede4] px-3 py-2">
          Any flights to Goa this Friday under ₹6k?
        </p>
        <p className="max-w-[80%] rounded-2xl rounded-bl-sm bg-white text-[#211d1b] px-3 py-2 shadow-sm">
          Found 3 options. The cheapest is ₹5,240 at 7:15 AM. Want me to email the itinerary?
        </p>
        <p className="ml-auto max-w-[60%] rounded-2xl rounded-br-sm bg-[#211d1b] text-[#f7ede4] px-3 py-2">Yes please!</p>
        <div className="flex gap-1 bg-white w-fit rounded-2xl px-3 py-2.5 shadow-sm">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#e8457a]"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const CodeWindow = () => (
  <div className="w-[220px] rounded-lg bg-[#161214] border border-white/10 shadow-[0_24px_48px_rgb(0_0_0/0.5)] overflow-hidden">
    <div className="flex gap-1 px-2.5 py-2 bg-white/5">
      <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
      <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
      <span className="w-2 h-2 rounded-full bg-[#28c840]" />
    </div>
    <pre className="px-3 py-2.5 text-[9px] leading-[1.6] text-[#f3e8de]/85 font-primary whitespace-pre">
      <span className="text-[#ff8fb3]">from</span> peft <span className="text-[#ff8fb3]">import</span> LoraConfig{"\n"}
      model = load(<span className="text-[#febc2e]">"llama-3.1-8b"</span>){"\n"}
      cfg = LoraConfig(r=<span className="text-[#9ad9ff]">16</span>, alpha=<span className="text-[#9ad9ff]">32</span>){"\n"}
      trainer.train(){"\n"}
      <span className="text-white/40"># eval acc: 0.87 ✓</span>
    </pre>
  </div>
);

const EvalPanel = () => (
  <div className="w-[210px] rounded-xl bg-[#f7ede4] p-3 shadow-[0_24px_48px_rgb(0_0_0/0.4)]">
    <p className="text-[10px] font-bold text-[#211d1b] mb-2">Judge scores · v2.3</p>
    {[
      ["Faithfulness", 92],
      ["Relevance", 88],
      ["Tone", 95],
    ].map(([label, score], i) => (
      <div key={label} className="mb-1.5">
        <div className="flex justify-between text-[9px] text-[#211d1b]/70">
          <span>{label}</span>
          <span>{score}</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#211d1b]/10 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#ff8fb3] to-[#d4386b]"
            initial={{ width: 0 }}
            whileInView={{ width: `${score}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: "easeOut" }}
          />
        </div>
      </div>
    ))}
    <p className="mt-2 text-[9px] text-[#1f9d55] font-semibold">✓ No regressions</p>
  </div>
);

const Terminal = ({ compact }) => (
  <div className={`${compact ? "w-[230px]" : "w-[300px]"} rounded-xl bg-[#0f0b0d] border border-white/10 shadow-[0_30px_60px_rgb(0_0_0/0.55)] overflow-hidden`}>
    <div className="flex items-center gap-1.5 px-3 py-2 bg-white/5">
      <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
      <span className="ml-2 text-[9px] text-white/40">uvicorn app:api</span>
    </div>
    <div className={`px-3 font-primary ${compact ? "py-2 text-[9px] leading-[1.7]" : "py-3 text-[10px] leading-[1.8]"}`}>
      {[
        ["POST", "/v1/chat", "200", "84ms"],
        ["GET", "/v1/docs/search", "200", "31ms"],
        ["POST", "/v1/grade", "200", "112ms"],
        ["GET", "/health", "200", "2ms"],
        ["POST", "/v1/embed", "201", "46ms"],
      ]
        .slice(0, compact ? 3 : 5)
        .map(([m, path, code, t], i) => (
        <motion.p
          key={path}
          className="flex gap-2 text-[#f3e8de]/80"
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 + i * 0.12 }}
        >
          <span className="text-[#ff8fb3] w-9">{m}</span>
          <span className="flex-1 truncate">{path}</span>
          <span className="text-[#28c840]">{code}</span>
          <span className="text-white/40 w-10 text-right">{t}</span>
        </motion.p>
      ))}
      <p className="text-white/50">
        ❯ <span className="inline-block w-1.5 h-3 align-middle bg-[#ff8fb3] animate-pulse" />
      </p>
    </div>
  </div>
);

// A generic landing page drawn in code: nav, hero, CTA, feature cards and a live chart.
const Website = () => (
  <div className="w-[330px] rounded-xl bg-[#f7ede4] shadow-[0_30px_60px_rgb(0_0_0/0.45)] overflow-hidden border border-white/20">
    <div className="flex items-center gap-1 px-3 py-2 bg-[#211d1b]">
      <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
      <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
      <span className="w-2 h-2 rounded-full bg-[#28c840]" />
      <span className="ml-3 flex-1 h-3 rounded-full bg-white/10 text-[7px] text-white/50 leading-3 px-2">app.ananya.dev</span>
    </div>
    <div className="px-4 pt-3 pb-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[9px] font-bold text-[#211d1b]">
          <span className="w-3 h-3 rounded-md bg-gradient-to-br from-[#ff8fb3] to-[#d4386b]" /> Insight
        </span>
        <span className="flex gap-2">
          {[10, 8, 12].map((w) => (
            <span key={w} className="h-1.5 rounded-full bg-[#211d1b]/15" style={{ width: w * 2 }} />
          ))}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-5 gap-3 items-center">
        <div className="col-span-3">
          <p className="text-[12px] leading-tight font-bold text-[#211d1b]">Your model, as a product people use.</p>
          <p className="mt-1.5 text-[7px] leading-snug text-[#211d1b]/60">Upload data, get predictions, share results in one click.</p>
          <div className="mt-2.5 flex gap-1.5">
            <span className="rounded-full bg-[#211d1b] text-[#f7ede4] text-[7px] font-semibold px-2.5 py-1">Get started</span>
            <span className="rounded-full border border-[#211d1b]/20 text-[#211d1b] text-[7px] px-2.5 py-1">Docs</span>
          </div>
        </div>
        <div className="col-span-2 rounded-lg bg-white p-2 shadow-sm">
          <div className="flex items-end gap-1 h-14">
            {[40, 65, 50, 80, 60, 95].map((h, i) => (
              <motion.span
                key={i}
                className="flex-1 rounded-sm bg-gradient-to-t from-[#d4386b] to-[#ff8fb3]"
                initial={{ height: "10%" }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 + i * 0.08, ease: "easeOut" }}
              />
            ))}
          </div>
          <p className="mt-1 text-[6px] text-[#211d1b]/50">Predictions / day</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {["React UI", "FastAPI", "Model"].map((label) => (
          <div key={label} className="rounded-lg bg-white/80 border border-[#211d1b]/5 p-2">
            <span className="block w-3 h-3 rounded bg-[#f6cfdc]" />
            <p className="mt-1.5 text-[7px] font-semibold text-[#211d1b]">{label}</p>
            <span className="mt-1 block h-1 w-3/4 rounded-full bg-[#211d1b]/10" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Vision = ({ image }) => (
  <div className="relative w-[220px] h-[135px] rounded-xl overflow-hidden shadow-[0_24px_48px_rgb(0_0_0/0.45)] border border-white/20">
    <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    {/* scanning line */}
    <motion.span
      className="absolute inset-x-0 h-px bg-[#ff8fb3] shadow-[0_0_12px_2px_#ff8fb3]"
      animate={{ top: ["8%", "92%", "8%"] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    />
    <span className="absolute left-2 top-2 rounded-full bg-black/60 text-white text-[9px] px-2 py-0.5">PSNR 21.1</span>
  </div>
);

const VISUALS = {
  chat: () => <Phone />,
  code: () => <CodeWindow />,
  eval: () => <EvalPanel />,
  terminal: (item) => <Terminal compact={item.size === "short"} />,
  website: () => <Website />,
  vision: (item) => <Vision image={item.image} />,
};

/* ---------- cards ---------- */

const ServiceCard = ({ item }) => {
  const tall = item.size === "tall";
  const Visual = VISUALS[item.visual];
  return (
    <motion.article
      whileHover="hover"
      className={`relative overflow-visible rounded-3xl text-white ${THEMES[item.theme]} ${
        tall ? "h-[540px] p-8 lg:p-10" : "h-[250px] p-6 lg:p-7"
      }`}
    >
      {/* soft sheen */}
      <div className="absolute inset-0 rounded-3xl bg-[linear-gradient(160deg,rgba(255,255,255,0.12),transparent_40%)] pointer-events-none" />

      <div className={`relative z-10 ${tall ? "" : "max-w-[48%]"}`}>
        <h3 className={`font-bold leading-tight ${tall ? "text-3xl lg:text-4xl" : "text-xl lg:text-2xl"}`}>
          {item.title}
        </h3>
        <p className={`mt-3 text-white/80 leading-relaxed ${tall ? "text-base max-w-sm" : "text-xs lg:text-sm"}`}>
          {item.text}
        </p>
      </div>

      {/* Mockups poke out past the bottom edge and rise a little on hover. */}
      <motion.div
        variants={{ hover: { y: -10, rotate: tall ? -1 : -2 } }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className={`absolute z-20 ${tall ? "left-1/2 -bottom-16" : "-right-4 -bottom-8"}`}
        style={tall ? { x: "-50%" } : undefined}
      >
        {Visual(item)}
      </motion.div>
    </motion.article>
  );
};

// Group consecutive short cards into stacked pairs; tall cards get their own column.
const toColumns = (items) => {
  const cols = [];
  items.forEach((item) => {
    const last = cols[cols.length - 1];
    if (item.size === "short" && last && last.length === 1 && last[0].size === "short") last.push(item);
    else cols.push([item]);
  });
  return cols;
};

const ArrowButton = ({ dir, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={dir < 0 ? "Previous" : "Next"}
    className="w-12 h-12 rounded-full border-2 border-accent text-accent flex items-center justify-center hover:bg-accent hover:text-primary transition-colors disabled:opacity-30 disabled:pointer-events-none"
  >
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir < 0 ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  </button>
);

const Services = () => {
  const scroller = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  const columns = toColumns(SERVICES.items);

  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);

  useEffect(() => {
    // Snap cards to the same inset as the page content. (A % scroll-padding would be
    // relative to the scroller itself, so mirror its computed padding instead.)
    const sync = () => {
      const el = scroller.current;
      if (!el) return;
      el.style.scrollPaddingInline = getComputedStyle(el).paddingLeft;
      el.scrollLeft = 0;
      update();
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [update]);

  const step = (dir) => {
    const el = scroller.current;
    const col = el.querySelector("[data-col]");
    const amount = col ? col.getBoundingClientRect().width + 32 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section id="services" className="pt-24 pb-10">
      <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <h2 className="font-bold text-5xl md:text-6xl leading-[1.05]">
          <span className="text-accent">What</span>
          <br />I excel at
        </h2>
        <div className="flex items-end gap-6">
          <p className="text-ink/65 max-w-md leading-relaxed text-sm md:text-base">{SERVICES.intro}</p>
          <div className="flex gap-3 shrink-0">
            <ArrowButton dir={-1} disabled={edges.start} onClick={() => step(-1)} />
            <ArrowButton dir={1} disabled={edges.end} onClick={() => step(1)} />
          </div>
        </div>
      </Reveal>

      {/* Bleeds to both screen edges (padding keeps the first card aligned with the content);
          bottom padding leaves room for mockups that overhang their cards. */}
      <div
        ref={scroller}
        onScroll={update}
        className="flex gap-8 overflow-x-auto snap-x snap-mandatory pb-20 pt-2 mx-[calc(50%-50vw)] px-[calc(50vw-50%)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {columns.map((col, i) => (
          <Reveal
            key={col[0].title}
            delay={Math.min(i, 3) * 0.08}
            className="snap-start shrink-0 w-[85vw] sm:w-[420px] lg:w-[460px] flex flex-col gap-8"
            data-col
          >
            {col.map((item) => (
              <ServiceCard key={item.title} item={item} />
            ))}
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default Services;
